import React, { useState } from 'react';
import Tooltip from './Tooltip';

interface Card {
  title: string;
  content: string | React.ReactNode;
}

const AboutSection: React.FC = () => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const cards: Card[] = [
    {
      title: "Beyond Species Counts: Why Metaomics Changes Everything",
      content: (
        <>
          At Ocean BioMetrics we don't just count species; we leverage cutting-edge molecular tools to reveal how marine ecosystems actually function and deliver value. Traditional surveys, whether visual or mechanical, capture only a small fraction of local biodiversity, the visible minority. Environmental DNA (eDNA) <Tooltip definition="An amplicon-based DNA method that sequences short, standardized genetic markers ('barcodes') from environmental samples to identify which species are present." term="Metabarcoding">metabarcoding</Tooltip> can massively expand this coverage to thousands of rare, cryptic, and evasive species, including the microbial communities that underpin ocean health. But we go further. Through <Tooltip definition="Sequencing all genetic material in an environmental sample, not just barcode regions, to identify the organisms present and detect functional genes that shed light on metabolic pathways, ecological roles, and potential interactions." term="Metagenomics">metagenomics</Tooltip> we reveal what key ecosystem players are actually doing: their functions, health status, ecological roles, and metabolic interdependencies. This allows us to move from biodiversity inventories to deep insights into ecosystem structure, resilience, and vulnerability.
        </>
      )
    },
    {
      title: "From Marine Complexity to Financial Solutions",
      content: (
        <>
          We turn comprehensive functional biodiversity data into clear and standardized metrics that enable performance-based finance, from biodiversity and carbon credits to nature-based bonds, debt swaps, and <Tooltip definition="Marine Genetic Resources (MGRs) are the genetic material contained in marine organisms, including microbes, plants, and animals, that have actual or potential value. MGRs may include genes coding for enzymes, peptides, or other biomolecules with applications in biotechnology, medicine, and industry." term="Marine Genetic Resources">Marine Genetic Resource</Tooltip> revenues. Our science-based metrics give marine protected areas, conservation groups, investors, and coastal nations the data and insights they need to make conservation impact measurable and biodiversity outcomes investable.
        </>
      )
    },
    {
      title: "Sustainable, Equitable, and Compliant",
      content: "We're pioneering a transformative approach to marine conservation by creating sustainable funding mechanisms that break the cycle of chronic funding gaps. Our model ensures local communities and nations benefit fairly from their ocean resources while meeting international standards for marine governance and benefit-sharing, establishing conservation finance that sustains long-term ocean protection and management."
    }
  ];

  const toggleExpanded = (index: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <section id="about" className="py-5" style={{
      background: `
        linear-gradient(180deg, rgba(30, 35, 40, 0.85) 0%, rgba(45, 52, 58, 0.85) 100%),
        url('/images/background-images/ocean-sunset.jpg')
      `,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      padding: '5rem 0',
      position: 'relative'
    }}>
      <div className="container">
        {/* Header Section with increased padding */}
        <div className="row justify-content-center mb-5">
          <div className="col-lg-10 text-center" style={{ padding: '0 2rem' }}>
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: 700,
              marginBottom: '2rem',
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
              color: '#EFEFE3'
            }}>
              About Ocean BioMetrics
            </h2>
            <p style={{
              fontSize: '1.25rem',
              color: '#D5D6CC',
              lineHeight: '1.8',
              maxWidth: '900px',
              margin: '0 auto',
              marginBottom: '3rem'
            }}>
              We're on a mission to transform how we understand, protect, and value our oceans through cutting-edge technology. By combining AI and advanced genomics, we decode marine ecosystems at the molecular level.
            </p>
          </div>
        </div>

        {/* Expandable List Section */}
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              maxWidth: '900px',
              margin: '0 auto'
            }}>
              {cards.map((card, index) => {
                const isExpanded = expandedItems.has(index);

                return (
                  <div
                    key={index}
                    style={{
                      background: 'rgba(239, 239, 227, 0.04)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(239, 239, 227, 0.08)',
                      borderRadius: '15px',
                      overflow: 'hidden',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: isExpanded
                        ? '0 15px 50px rgba(8, 136, 208, 0.15)'
                        : '0 5px 20px rgba(0,0,0,0.15)',
                      borderColor: isExpanded
                        ? 'rgba(8, 136, 208, 0.3)'
                        : 'rgba(239, 239, 227, 0.08)'
                    }}
                  >
                    <div
                      onClick={() => toggleExpanded(index)}
                      style={{
                        padding: window.innerWidth < 768 ? '1rem 1rem' : '1.5rem 2rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        userSelect: 'none',
                        position: 'relative',
                        transition: 'background 0.3s ease',
                        background: isExpanded
                          ? 'rgba(239, 239, 227, 0.02)'
                          : 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (!isExpanded) {
                          e.currentTarget.style.background = 'rgba(239, 239, 227, 0.02)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isExpanded) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: window.innerWidth < 768 ? '0.75rem' : '1.5rem', flex: 1 }}>
                        {/* Item Number */}
                        <div style={{
                          fontSize: window.innerWidth < 768 ? '1.2rem' : '1.5rem',
                          fontWeight: 700,
                          color: '#18C3BA',
                          fontFamily: "'Montserrat', sans-serif",
                          minWidth: window.innerWidth < 768 ? '28px' : '35px'
                        }}>
                          0{index + 1}
                        </div>

                        {/* Title */}
                        <h3 style={{
                          fontSize: window.innerWidth < 768 ? 'clamp(0.9rem, 3vw, 1rem)' : 'clamp(1rem, 2vw, 1.2rem)',
                          fontWeight: 600,
                          color: '#EFEFE3',
                          margin: 0,
                          lineHeight: 1.4,
                          fontFamily: "'Montserrat', sans-serif",
                          paddingRight: window.innerWidth < 768 ? '2.5rem' : '0'
                        }}>
                          {card.title}
                        </h3>
                      </div>

                      {/* Plus/Minus Icon */}
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: isExpanded
                          ? 'rgba(8, 136, 208, 0.15)'
                          : 'rgba(239, 239, 227, 0.05)',
                        border: isExpanded
                          ? '1px solid rgba(8, 136, 208, 0.3)'
                          : '1px solid rgba(239, 239, 227, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        flexShrink: 0
                      }}>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          style={{
                            transition: 'transform 0.3s ease',
                            transform: isExpanded ? 'rotate(45deg)' : 'rotate(0deg)'
                          }}
                        >
                          <path
                            d="M8 3V13M3 8H13"
                            stroke={isExpanded ? '#0888D0' : '#D5D6CC'}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* Expandable Content */}
                    <div style={{
                      maxHeight: isExpanded ? (window.innerWidth < 768 ? '600px' : '450px') : '0',
                      opacity: isExpanded ? 1 : 0,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        padding: window.innerWidth < 768 ? '1rem 1rem 1.25rem 1rem' : '1rem 2rem 1.25rem 4.5rem',
                        transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)',
                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderTop: '1px solid rgba(239, 239, 227, 0.08)',
                        marginTop: '0.5rem'
                      }}>
                        <p style={{
                          fontSize: window.innerWidth < 768 ? '0.95rem' : '1rem',
                          color: '#D5D6CC',
                          lineHeight: window.innerWidth < 768 ? '1.7' : '1.6',
                          margin: 0,
                          opacity: 0.9
                        }}>
                          {card.content}
                        </p>
                      </div>
                    </div>

                    {/* Bottom accent line */}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: 'linear-gradient(90deg, rgba(8, 136, 208, 0.6), rgba(8, 136, 208, 0))',
                      opacity: isExpanded ? 1 : 0,
                      transition: 'opacity 0.3s ease'
                    }} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;